import { match } from 'path-to-regexp';
import ROUTES_PATH, { PARAMS } from 'domain/router/routesPathConfig';

// FILTER ROUTES TREE BY RESTRICTION
/**
 * Recursively filters routes tree based on ACL restriction function
 * @param {Function} ACLFilterFN - Function to check if route is available
 * @param {Array} routes - Array of route objects to filter
 * @returns {Array} Filtered routes array
 */
export const routesByRestriction = (ACLFilterFN, routes) =>
  routes.reduce((acc, route) => {
    const isAvailable = ACLFilterFN(route);

    if (isAvailable) {
      const filteredRoute = route.children
        ? { ...route, children: routesByRestriction(ACLFilterFN, route.children) }
        : route;

      acc.push(filteredRoute);
    }

    return acc;
  }, []);

/**
 * Checks if a given path matches the current pathname
 * be careful, will return the first match
 * /company/confidential/:COMPANY_ID/:CATEGORY1/:CATEGORY2?/:CATEGORY3?, /company/confidential/:COMPANY_ID - return first
 * @param {string} path - Route path pattern (e.g., /company/confidential/:COMPANY_ID/:CATEGORY1/:CATEGORY2?/:CATEGORY3?)
 * @param {string} [pathname=window.location.pathname] - Pathname to match against (/company/confidential/6869691756/2/6)
 * @returns {boolean} True if path matches pathname
 */

/**
 * Determines if a given route path matches the current or specified pathname.
 *
 * This function checks if the provided `path` matches the `pathname`, optionally
 * considering match options. It handles edge cases like root routes and wildcard
 * patterns (`/*`). Routes ending with `/*` are skipped as they are typically used
 * as root routes for nested children and cannot be parsed using the `path-to-regexp`
 * library.
 *
 * @param {string} path - The route path to match.
 * @param {string} [pathname=window.location.pathname] - The pathname to match against. Defaults to the current window location's pathname.
 * @param {object} [options={}] - Optional configuration for path matching, passed to the `path-to-regexp` match function.
 * @returns {boolean} Returns `true` if the path matches the pathname; otherwise, returns `false`.
 */
export const isMatchRoute = (path, pathname = window.location.pathname, options = {}) => {
  // Handle non-string paths (root routes)
  if (typeof path !== 'string') return false; // Root route has no path

  // Skip routes ending with '/*' as path-to-regexp can't parse them
  // These are typically root routes for nested children
  // const p = path.replace('/*', '/(.*)');
  // path-to-regexp lib cant parse '/*' - so we skip route with '/*' - its root route for children routes
  // be careful with this behaviour
  // https://github.com/serverless-nextjs/serverless-next.js/issues/2433
  // https://github.com/pillarjs/path-to-regexp/pull/270
  if (path.endsWith('/*')) return false;

  try {
    const matchFn = match(path, { decode: decodeURIComponent, ...options });
    return Boolean(matchFn(pathname));
  } catch (error) {
    // Log error for debugging but don't throw
    console.warn(`Failed to match route path: ${path}`, error);
    return false;
  }
};

// Like isMatchRoute, but returns the decoded route params (or null if no match).
// Same skip rules: root/wildcard routes ending in `/*` aren't parseable by path-to-regexp.
export const matchRouteParams = (path, pathname = window.location.pathname, options = {}) => {
  if (typeof path !== 'string' || path.endsWith('/*')) return null;

  try {
    const matchFn = match(path, { decode: decodeURIComponent, ...options });
    const result = matchFn(pathname);
    return result ? result.params : null;
  } catch (error) {
    console.warn(`Failed to match route path: ${path}`, error);
    return null;
  }
};

// The company-workspace document-list lives at four route shapes: standard /
// confidential, each with an explicit category and a category-undefined entry
// point (before the redirect to a default category).
const COMPANY_WORKSPACE_ROUTE_PATHS = [
  ROUTES_PATH.COMPANY_WORKSPACE.absolute,
  ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute,
  ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute,
  ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED.absolute,
];

/**
 * Route matching utilities for specific route types
 */
export const matchRoute = {
  isDocument: (pathname = window.location.pathname) => {
    return isMatchRoute(ROUTES_PATH.DOCUMENT.absolute, pathname);
  },
  isPayment: (pathname) => {
    return isMatchRoute(ROUTES_PATH.PAY_PAYMENT.absolute, pathname);
  },

  isCompanyWorkspace: (pathname = window.location.pathname) => {
    const isConfidential = pathname.toLowerCase().includes('confidential');
    const routePath = isConfidential
      ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
      : ROUTES_PATH.COMPANY_WORKSPACE.absolute;

    return isMatchRoute(routePath, pathname);
  },

  isCompanyDashboard: (pathname = window.location.pathname) => {
    return isMatchRoute(ROUTES_PATH.COMPANY_DASHBOARD.absolute, pathname);
  },
  isPaymentWorkspace: (pathname) => {
    return isMatchRoute(ROUTES_PATH.PAY_COMPANY.absolute, pathname);
  },

  isLogin: (pathname = window.location.pathname) => {
    return isMatchRoute(ROUTES_PATH.AUTH_LOGIN.absolute, pathname);
  },

  canBeAccessedUnauthorizedRoute: (pathname = window.location.pathname) => {
    const unauthorizedPaths = [
      ROUTES_PATH.NOT_SUPPORTED_MOBILE_BROWSER.absolute,
      ROUTES_PATH.NOT_SUPPORTED_BROWSER.absolute,
      ROUTES_PATH.NOT_SUPPORTED_IPAD.absolute,
      ROUTES_PATH.ACCESS_LIMITED.absolute,
    ];

    return unauthorizedPaths.some((path) => isMatchRoute(path, pathname));
  },
};

/**
 * Gets the current route configuration object
 * @param {string} [pathname=window.location.pathname] - Pathname to match
 * @param {object} [options={}] - Optional configuration for path matching, passed to the `path-to-regexp` match function.
 * @returns {Object|null} Route configuration object or null if not found
 */
export const getCurrentRoute = (pathname = window.location.pathname, options = {}) => {
  return Object.values(ROUTES_PATH).find((route) => isMatchRoute(route.absolute, pathname, options)) || null;
};

// Unread-counter "visit key" for a pathname: COMPANY_LIST (the root list `/`),
// DOCUMENT_LIST:<companyId> (a company workspace — companyId included so switching
// companies is a new visit, but category/filter changes on the same company keep
// the same key), or OTHER (settings, document view, dashboard…). The chat unread
// refetch fires only when this key changes INTO a list key (see chatNotifications
// saga), so category/filter changes are ignored while real navigations re-fetch.
export const getUnreadVisitKey = (pathname = window.location.pathname) => {
  if (isMatchRoute(ROUTES_PATH.COMPANIES.absolute, pathname)) return 'COMPANY_LIST';

  // Any of the four company-workspace shapes (standard/confidential ×
  // category-scoped/category-undefined) is a document-list visit. Match against the
  // route configs and read companyId from the decoded params — no manual URL parsing.
  const workspaceParams = COMPANY_WORKSPACE_ROUTE_PATHS.map((path) => matchRouteParams(path, pathname)).find(Boolean);

  if (workspaceParams) {
    const companyId = workspaceParams[PARAMS.COMPANY_ID];
    return `DOCUMENT_LIST:${companyId || ''}`;
  }

  return 'OTHER';
};

const LIST_VISIT_KEY_PREFIXES = ['COMPANY_LIST', 'DOCUMENT_LIST:'];

// Whether a visit key is a list view (one that should refetch unread counts on entry).
export const isListVisitKey = (key) => LIST_VISIT_KEY_PREFIXES.some((p) => key.startsWith(p));
